/**
 * React hook for transcribing audio using Azure OpenAI speech-to-text
 *
 * Wraps the API client's transcribeAudio method with TanStack Query mutation
 * for automatic state management and error handling.
 */

import { useMutation } from "@tanstack/react-query";
import { createApiClient, ApiClientError } from "@floline/api-client";
import type { TranscriptionResponse } from "@floline/api-client/types";
import { API_URL } from "@/env";
import { i18n } from "@/i18n";
import { mapConsentRequiredError } from "@/lib/consent-errors";

// ============================================================================
// API Client
// ============================================================================

/** API client configured for transcription requests */
const apiClient = createApiClient(
  API_URL
);

// ============================================================================
// Types
// ============================================================================

export interface TranscriptionInput {
  /** Audio blob to transcribe */
  blob: Blob;
  /** Language hint in ISO-639-1 format (e.g., "en", "fi") - improves accuracy */
  language?: string;
}

export interface UseTranscriptionOptions {
  /** Callback when transcription succeeds */
  onSuccess?: (result: TranscriptionResponse) => void;
  /** Callback when transcription fails */
  onError?: (error: Error) => void;
}

export interface UseTranscriptionReturn {
  /** Transcribe an audio file */
  transcribe: (input: TranscriptionInput) => Promise<TranscriptionResponse>;
  /** Whether transcription is in progress */
  isTranscribing: boolean;
  /** Transcription state */
  transcriptionState: "idle" | "transcribing" | "success" | "error";
  /** The last successful transcription result */
  result: TranscriptionResponse | undefined;
  /** Transcription error if any */
  error: Error | null;
  /** Reset the transcription state */
  reset: () => void;
}

// ============================================================================
// Transcription Function
// ============================================================================

/**
 * Transcribe audio blob using the API
 *
 * @param input - Audio blob and optional language hint
 * @returns Transcription result with text and metadata
 */
async function transcribeAudioFile(
  input: TranscriptionInput
): Promise<TranscriptionResponse> {
  try {
    return await apiClient.transcribeAudio(input.blob, {
      language: input.language,
    });
  } catch (error: unknown) {
    const consentError = mapConsentRequiredError(error, i18n.t.bind(i18n));
    if (consentError) {
      throw consentError;
    }

    // Rethrow with more descriptive message for common errors
    if (error instanceof ApiClientError) {
      if (error.status === 401) {
        throw new Error(i18n.t("errors.signInToTranscribe"));
      }
      if (error.status === 503 || error.code === "SERVICE_UNAVAILABLE") {
        throw new Error(i18n.t("errors.transcriptionUnavailable"));
      }
      if (error.code === "UNSUPPORTED_FORMAT") {
        throw new Error(i18n.t("errors.unsupportedAudioFormat"));
      }
      if (error.code === "TRANSCRIPTION_FAILED") {
        throw new Error(
          error.message || i18n.t("errors.transcriptionFailed")
        );
      }
    }
    throw error;
  }
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook for transcribing audio recordings to text
 *
 * Uses Azure OpenAI's speech-to-text models (gpt-4o-transcribe or whisper).
 *
 * @example
 * ```tsx
 * const { transcribe, isTranscribing, result, error } = useTranscription({
 *   onSuccess: (result) => {
 *     console.log('Transcribed:', result.text);
 *   },
 *   onError: (error) => {
 *     console.error('Transcription failed:', error);
 *   },
 * });
 *
 * // Transcribe a recording
 * const result = await transcribe({
 *   blob: audioBlob,
 *   language: 'en',
 * });
 * ```
 */
export function useTranscription(
  options: UseTranscriptionOptions = {}
): UseTranscriptionReturn {
  const { onSuccess, onError } = options;

  const mutation = useMutation({
    mutationFn: transcribeAudioFile,
    onSuccess,
    onError,
  });

  // Derive transcription state from mutation status
  const transcriptionState = mutation.isPending
    ? "transcribing"
    : mutation.isSuccess
      ? "success"
      : mutation.isError
        ? "error"
        : "idle";

  return {
    transcribe: mutation.mutateAsync,
    isTranscribing: mutation.isPending,
    transcriptionState,
    result: mutation.data,
    error: mutation.error,
    reset: mutation.reset,
  };
}

// ============================================================================
// Query Keys (for potential future use)
// ============================================================================

/** Query key factory for transcriptions */
export const transcriptionKeys = {
  all: ["transcriptions"] as const,
} as const;
